import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { DataSource } from '@/entities/all';

const initialForm = (source) => ({
  source_name: source?.source_name || '',
  description: source?.description || '',
  trust_score: source?.trust_score ?? 0.8,
  status: source?.stored_status || source?.status || 'Active',
  is_golden_source: source?.is_golden_source || false,
  is_etl_used: source?.meta?.etl?.enabled || false,
  s3_region: source?.meta?.etl?.s3?.region || '',
  s3_bucket: source?.meta?.etl?.s3?.bucket || '',
  s3_path: source?.meta?.etl?.s3?.path || '',
});

export default function EditDataSourceModal({ isOpen, onClose, onSuccess, source }) {
  // Concrete strings from the start — formData.source_name must never be
  // undefined (controlled/uncontrolled input flip).
  const [formData, setFormData] = useState(() => initialForm(source));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [etlTouched, setEtlTouched] = useState(false);

  useEffect(() => {
    if (source && isOpen) { setFormData(initialForm(source)); setEtlTouched(false); setError(null); }
  }, [source, isOpen]);

  const handleChange = (field, value) => {
    if (field === 'is_etl_used' || field.startsWith('s3_')) setEtlTouched(true);
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      const { s3_region, s3_bucket, s3_path, is_etl_used, ...otherFields } = formData;
      const patch = { ...otherFields };
      // Only rewrite meta.etl when the user actually touched the ETL section —
      // an unconditional rewrite clobbered other meta.etl sub-keys on every
      // ordinary edit.
      if (etlTouched) {
        patch.meta = {
          ...source.meta,
          etl: {
            ...source.meta?.etl,
            enabled: is_etl_used,
            s3: { region: s3_region, bucket: s3_bucket, path: s3_path },
          },
        };
      }
      // The unified Sources list carries the numeric row id in entity_id;
      // updating by the namespaced 'ds-<n>' id matched nothing (silent 404).
      const entityId = source.entity_id ?? source.id;
      await DataSource.update(entityId, patch);
      onSuccess();
    } catch (err) {
      console.error('Failed to update data source', err);
      setError(err?.message || 'Failed to update data source');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Data Source</DialogTitle>
          <DialogDescription>Update the details for '{source?.source_name}'.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6 pt-4">
          <div>
            <Label htmlFor="edit_source_name">Source Name</Label>
            <Input id="edit_source_name" value={formData.source_name} onChange={e => handleChange('source_name', e.target.value)} required />
          </div>
          <div>
            <Label htmlFor="edit_description">Description</Label>
            <Textarea id="edit_description" value={formData.description} onChange={e => handleChange('description', e.target.value)} />
          </div>
          <div className="flex items-center space-x-2">
            <Switch id="edit_is_etl_used" checked={formData.is_etl_used} onCheckedChange={v => handleChange('is_etl_used', v)} />
            <Label htmlFor="edit_is_etl_used">Is source S3 bucket</Label>
          </div>
          {formData.is_etl_used && (
            <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
              <h3 className="text-sm font-medium">S3 Source File Location</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit_s3_region">Region</Label>
                  <Input id="edit_s3_region" value={formData.s3_region} onChange={e => handleChange('s3_region', e.target.value)} placeholder="e.g., us-east-1" />
                </div>
                <div>
                  <Label htmlFor="edit_s3_bucket">Bucket</Label>
                  <Input id="edit_s3_bucket" value={formData.s3_bucket} onChange={e => handleChange('s3_bucket', e.target.value)} placeholder="e.g., my-data-bucket" />
                </div>
              </div>
              <div>
                <Label htmlFor="edit_s3_path">Path to Parent Directory</Label>
                <Input id="edit_s3_path" value={formData.s3_path} onChange={e => handleChange('s3_path', e.target.value)} placeholder="e.g., data/raw/source-files/" />
              </div>
            </div>
          )}
          <div>
            <Label htmlFor="edit_status">Status</Label>
            <Select value={formData.status} onValueChange={v => handleChange('status', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Maintenance">Maintenance</SelectItem>
                <SelectItem value="Deprecated">Deprecated</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Trust Score: {((formData.trust_score || 0) * 100).toFixed(0)}%</Label>
            <Slider value={[formData.trust_score || 0]} onValueChange={v => handleChange('trust_score', v[0])} max={1} step={0.01} />
          </div>
          <div className="flex items-center space-x-2">
            <Switch id="edit_is_golden_source" checked={formData.is_golden_source} onCheckedChange={v => handleChange('is_golden_source', v)} />
            <Label htmlFor="edit_is_golden_source">Is this a golden source?</Label>
          </div>
          {error && <div className="text-sm text-red-600">{error}</div>}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={isSubmitting}>{isSubmitting ? 'Updating...' : 'Update Source'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
